import { ProductSlugs } from '@deque/billing-utils';
import { Button, Checkbox, Loader, TextField } from '@deque/cauldron-react';
import emailValidator from 'email-validator';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACTIVE_STATES,
  HIDDEN_USER_MANAGEMENT_PRODUCTS,
  PRODUCTS_WITHOUT_USER_MANAGEMENT
} from '~/common/constants';
import { useProducts } from '~/common/contexts/products';
import type { SelectedEnterprise } from '../../pages/internal/admin/types';
import styles from './AddUserForm.css';

export interface AddUserFormData {
  email: string;
  firstName: string;
  lastName: string;
  isEnterpriseAdmin: boolean;
  products: Record<string, boolean>;
  productAdmins: Record<string, boolean>;
  sendEmail: boolean;
}

// Keep in sync with PRODUCTS_WITH_ACCESS_LEVEL_MANAGEMENT in packages/server/src/constants.ts
const PRODUCTS_WITH_APP_ADMIN: string[] = [
  ProductSlugs.axeMonitor,
  ProductSlugs.axeReports,
  ProductSlugs.axeAuditor,
  ProductSlugs.axeDevToolsMobile
];

interface AddUserFormProps {
  preloadCustomerData: SelectedEnterprise;
  onSubmit: (data: AddUserFormData) => Promise<void>;
  onCancel: () => void;
}

const AddUserForm: React.FC<AddUserFormProps> = ({
  preloadCustomerData,
  onSubmit,
  onCancel
}) => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isEnterpriseAdmin, setIsEnterpriseAdmin] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, boolean>
  >({});
  const [productAdmins, setProductAdmins] = useState<Record<string, boolean>>(
    {}
  );
  const [submitting, setSubmitting] = useState(false);

  const products = preloadCustomerData.enterpriseExplorer.products;
  const productSlugs = Object.values(products)
    .map(data => data.subscription.product_slug)
    .filter(
      slug =>
        !(PRODUCTS_WITHOUT_USER_MANAGEMENT as readonly string[]).includes(
          slug
        ) &&
        !(HIDDEN_USER_MANAGEMENT_PRODUCTS as readonly string[]).includes(slug)
    );

  const adminEligibleSlugs = productSlugs.filter(slug => {
    const productData = Object.values(products).find(
      data => data.subscription.product_slug === slug
    );
    return (
      selectedProducts[slug] &&
      PRODUCTS_WITH_APP_ADMIN.includes(slug) &&
      (ACTIVE_STATES as readonly string[]).includes(
        productData?.subscription?.purchase_state ?? ''
      )
    );
  });

  const handleProductToggle = useCallback((slug: string, checked: boolean) => {
    setSelectedProducts(prev => ({ ...prev, [slug]: checked }));
    if (!checked) {
      setProductAdmins(prev => ({ ...prev, [slug]: false }));
    }
  }, []);

  const handleProductAdminToggle = useCallback(
    (slug: string, checked: boolean) => {
      setProductAdmins(prev => ({ ...prev, [slug]: checked }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setEmailError(null);

      const trimmedEmail = email.trim();

      if (!trimmedEmail) {
        setEmailError(t('Email is required'));
        return;
      }

      if (!emailValidator.validate(trimmedEmail)) {
        setEmailError(t('Please enter a valid email address'));
        return;
      }

      setSubmitting(true);
      try {
        await onSubmit({
          email: trimmedEmail.toLowerCase(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          isEnterpriseAdmin,
          products: selectedProducts,
          productAdmins: Object.fromEntries(
            Object.entries(productAdmins).filter(([, v]) => v)
          ),
          sendEmail
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      email,
      firstName,
      lastName,
      isEnterpriseAdmin,
      selectedProducts,
      productAdmins,
      sendEmail,
      onSubmit,
      t
    ]
  );

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        label={t('Email')}
        name="email"
        type="email"
        required
        value={email}
        onChange={(value: string) => setEmail(value)}
        error={emailError || undefined}
      />

      <TextField
        label={t('First Name')}
        name="firstName"
        value={firstName}
        onChange={(value: string) => setFirstName(value)}
      />

      <TextField
        label={t('Last Name')}
        name="lastName"
        value={lastName}
        onChange={(value: string) => setLastName(value)}
      />

      <Checkbox
        id="add-user-admin-checkbox"
        label={t('Enterprise Admin')}
        name="isEnterpriseAdmin"
        checked={isEnterpriseAdmin}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setIsEnterpriseAdmin(e.target.checked)
        }
      />

      <Checkbox
        id="add-user-send-email-checkbox"
        label={t('Send Email')}
        name="sendEmail"
        checked={sendEmail}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSendEmail(e.target.checked)
        }
      />

      {productSlugs.length > 0 && (
        <fieldset>
          <legend>{t('Products')}</legend>
          {productSlugs.map(slug => (
            <Checkbox
              key={slug}
              id={`add-user-product-${slug}`}
              label={getProductBySlug(slug)?.name || slug}
              name={`product-${slug}`}
              checked={!!selectedProducts[slug]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleProductToggle(slug, e.target.checked)
              }
            />
          ))}
        </fieldset>
      )}

      {adminEligibleSlugs.length > 0 && (
        <fieldset>
          <legend>{t('Application Admin')}</legend>
          <p>{t('Set application admin for selected products.')}</p>
          {adminEligibleSlugs.map(slug => (
            <Checkbox
              key={slug}
              id={`add-user-product-admin-${slug}`}
              label={getProductBySlug(slug)?.name || slug}
              name={`product-admin-${slug}`}
              checked={!!productAdmins[slug]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleProductAdminToggle(slug, e.target.checked)
              }
            />
          ))}
        </fieldset>
      )}

      <div className={styles.AddUserForm__actions}>
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? <Loader label={t('Adding user...')} /> : t('Add User')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          {t('Cancel')}
        </Button>
      </div>
    </form>
  );
};

export default AddUserForm;
